import type { SyntheticEvent } from 'react';
import { Flex } from 'antd';
import InteractiveCard from '../../common/interactiveCard';
import { IMatter } from '../../../interface';
import { Briefcase } from '../../icons';
import { Heading, Text, Tooltip } from '../../Ant';
import { getMatterIconClass, getMatterLabel } from '../../../constants/utilityFunctions';
import { useTranslation } from 'react-i18next';
import { useIsTextTruncated } from '../../../hooks/useIsTextTruncated';

interface IMailMatterCardProps {
  matter: IMatter;
  onClick: (matter: IMatter, event: SyntheticEvent) => void;
  isSelected: boolean;
}
const MailMatterCard = ({ matter, onClick, isSelected }: IMailMatterCardProps) => {
  const principalClamp = useIsTextTruncated();
  const subjectClamp = useIsTextTruncated();

  /**
   * Only a clipped line has something left to reveal, so that is the only line that gets a title.
   * The tooltip stays mounted either way: swapping the wrapper in and out would remount the line
   * and detach the ref measuring it, which the measurement then re-triggers — an update loop at
   * any width where the line sits on the edge of clipping.
   */
  const revealWhenClipped = (element: React.ReactElement, text: string, isTruncated: boolean) => (
    <Tooltip title={isTruncated && text ? text : undefined} placement="top" arrow={false}>
      {element}
    </Tooltip>
  );

  const { t } = useTranslation();

  const matterSubject = matter?.matterNumber + ' - ' + matter?.subject;
  const principalName =
    matter?.principalCompany?.name ?? matter?.principalPrivatePerson?.fullName ?? '';
  return (
    <InteractiveCard
      onActivate={(event) => {
        onClick(matter, event);
      }}
      className={`matter-card-wrapper ${isSelected ? 'transaction-active-card' : ''}`}
    >
      <Briefcase className={getMatterIconClass(matter?.invoiceableType ?? '')} />
      <Flex vertical>
        <Heading
          level={6}
          $fontsize="11px"
          $color="description"
          $weight={400}
          $lineheight="16px"
          className="ta-left"
        >
          {getMatterLabel(t, matter?.invoiceableType)}
        </Heading>
        <Flex gap={2} vertical align="flex-start">
          {revealWhenClipped(
            <Text
              ref={principalClamp.ref}
              $fontsize="13px"
              $color="title"
              $weight={500}
              $lineheight="18px"
              className="ta-left lineclamp-1"
            >
              {principalName}
            </Text>,
            principalName,
            principalClamp.isTruncated,
          )}
          {revealWhenClipped(
            <Text
              ref={subjectClamp.ref}
              $fontsize="12px"
              $color="subtitle"
              $weight={400}
              $lineheight="18px"
              className="ta-left lineclamp-1"
            >
              {matterSubject}
            </Text>,
            matterSubject,
            subjectClamp.isTruncated,
          )}
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};
export default MailMatterCard;
